import { Link, useNavigate } from '@tanstack/react-router';
import { ChevronRight, PackageOpen, Zap } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { ChannelChip, DateChip, SourceDestinationChip } from '~/components/common/chips';
import { SegmentedTabs } from '~/components/common/segmented-tabs';
import { Skeleton } from '~/components/ui/skeleton';
import { CatalogOperationsLayout } from '~/features/catalog-operations/components/catalog-operations-layout';
import { StatusChip } from '~/features/catalog-operations/components/chips';
import { ProductsListView } from '~/features/catalog-operations/components/products-list-view';
import {
  useBatchCoverageDetail,
  useBatchHeader,
  useCurationRunFilters,
} from '~/features/catalog-operations/hooks/use-batch-detail';
import { useCurationProducts } from '~/features/catalog-operations/hooks/use-curation-products';
import { useProductEnrichmentStatus } from '~/features/catalog-operations/hooks/use-product-enrichment-status';
import {
  tagsTrendsScope,
  useProductTagsTrends,
} from '~/features/catalog-operations/hooks/use-product-tags-trends';
import { useProductsListingStore } from '~/features/catalog-operations/store/products-listing.store';
import type {
  ProductFilterValues,
  ProductTab,
} from '~/features/catalog-operations/types/catalog-operations.types';
import {
  batchFlowIcons,
  isExperimentSource,
} from '~/features/catalog-operations/utils/batch-flow-icons';
import { countAppliedFilters } from '~/features/catalog-operations/utils/product-filters';
import { ExportDownloadButton } from '~/features/exports/components/export-download-button';
import { cn } from '~/utils/cn';

const PRODUCT_TABS: { value: ProductTab; label: string }[] = [
  { value: 'newly-enriched', label: 'Newly Enriched Products' },
  { value: 'needs-attention', label: 'Needs Attention' },
];

/** What a scope segment's count pill renders: a single population, or an
 *  `enriched/total` pair when the job's enriched figure disagrees with the
 *  products list's own newly-enriched count. */
type ScopeBadge =
  | { kind: 'count'; value: number }
  | { kind: 'ratio'; enriched: number; total: number };

interface Props {
  batchId: string;
  initialTab: ProductTab;
}

/** Drill-in products page for a batch — shows products by enrichment status, grouped under tabs. */
export function ProductsPage({ batchId, initialTab }: Props) {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ProductTab>(initialTab);
  // Search/filters/viewMode/pagination/sort all live in a store (not local
  // state) so the listing survives navigating into a product's detail page
  // and back — see products-listing.store.ts. `activeTab` stays local/URL-seeded
  // (unchanged) since it already round-trips correctly via the route's search param.
  // Scoped per batch: this page is a template instantiated once per `batchId`,
  // so the store must reset whenever a different batch's page reads it —
  // done synchronously here, before any other selector, to avoid a flash of
  // stale-batch data.
  const storeBatchId = useProductsListingStore((s) => s.batchId);
  const ensureBatch = useProductsListingStore((s) => s.ensureBatch);
  if (storeBatchId !== batchId) ensureBatch(batchId);
  /** Drawer-applied filters (excludes the implicit `needs_attention` tab flag). */
  const appliedFilters = useProductsListingStore((s) => s.filters);
  const applyFilters = useProductsListingStore((s) => s.applyFilters);
  /** Committed free-text search (set by the search input on Enter / clear). */
  const search = useProductsListingStore((s) => s.search);
  const setSearch = useProductsListingStore((s) => s.setSearch);
  const viewMode = useProductsListingStore((s) => s.viewMode);
  const setViewMode = useProductsListingStore((s) => s.setViewMode);
  const pagination = useProductsListingStore((s) => s.pagination);
  const setPagination = useProductsListingStore((s) => s.setPagination);
  /** Active table sort — `null` column defers to the backend default (created_at). */
  const sortBy = useProductsListingStore((s) => s.sortBy);
  const sortDir = useProductsListingStore((s) => s.sortDir);
  const setSort = useProductsListingStore((s) => s.setSort);
  const resetPage = useProductsListingStore((s) => s.resetPage);
  const page = pagination.page;
  const pageSize = pagination.pageSize;

  // Header powers the page chrome AND supplies `curationRefId` — the single
  // source of truth for the curation API queries. The light phase-1 header, not
  // the heavy `/jobs/{id}` aggregate; shares the batch-detail page's cache.
  const { data: batch, isLoading: batchLoading } = useBatchHeader(batchId);
  const curationRefId = batch?.curationRefId;
  const isTrial = batch?.isTrial ?? false;

  // The enrichment-gap badge (further below) compares the job's batch-level
  // enriched figure against the list's newly-enriched count. That figure lives
  // on the coverage phase, and the comparison is only valid on an unnarrowed
  // list — so fetch `/coverage` only then, keeping the heavier read off the
  // common path.
  const isListNarrowed = countAppliedFilters(appliedFilters) > 0 || search.trim() !== '';
  const { data: batchCoverage } = useBatchCoverageDetail(batchId, batch, !isListNarrowed);

  // CSV-export inputs derived from the batch. `name` can be an empty string, so
  // fall back to the batchId for the export label.
  let exportLabel = batch?.batchId ?? '';
  if (batch?.name) exportLabel = batch.name;
  const exportConnector: 'META' | 'GMC' =
    batch?.connectorCode && /META|FACEBOOK/i.test(batch.connectorCode) ? 'META' : 'GMC';

  // Trial batches only ever surface Newly Enriched — Needs Attention isn't a
  // concept for them. Compute the effective tab synchronously (rather than
  // waiting on the redirect effect below) so there's no flash of the
  // Needs Attention tab/content while `batch` is still loading.
  const effectiveTab: ProductTab =
    isTrial && activeTab === 'needs-attention' ? 'newly-enriched' : activeTab;
  const visibleTabs = isTrial
    ? PRODUCT_TABS.filter((tab) => tab.value === 'newly-enriched')
    : PRODUCT_TABS;

  // Filter definitions — discovered from this run's own products (fields that
  // carry a value, options/bounds from those products). Stays disabled until
  // the batch resolves its run and connector.
  const { data: filterDefinitions } = useCurationRunFilters(curationRefId, batch?.connectorCode);

  // Merge drawer filters with the tab-driven flag. `needs_attention=true`
  // is added when the Needs Attention tab is active; the drawer hides this
  // key so users can't conflict with the tab.
  const effectiveFilters = useMemo<ProductFilterValues>(
    () => ({
      needs_attention: false,
      ...appliedFilters,
      ...(effectiveTab === 'needs-attention' ? { needs_attention: true } : {}),
    }),
    [appliedFilters, effectiveTab],
  );

  // Both tabs hit the same curation products endpoint; filters are merged
  // and serialised by the service. The hook stays disabled until
  // `curationRefId` resolves.
  const {
    data: productsResult,
    isLoading: productsLoading,
    isFetching: productsFetching,
  } = useCurationProducts({
    curationRefId,
    batchId,
    page,
    size: pageSize,
    filters: effectiveFilters,
    search,
    sortBy,
    sortDir,
  });

  const rawProducts = productsResult?.products ?? [];
  const total = productsResult?.total ?? 0;

  // Self-corrects a persisted page that's now out of range for a shrunk
  // result set. Unlike a client-paginated table, each page here is a real
  // server fetch, so only clamping the *display* value would desync the
  // pagination footer from the (still stale, still-being-fetched) row
  // window — this corrects the store instead, which triggers a real refetch.
  useEffect(() => {
    if (!productsResult) return;
    const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));
    if (pagination.page > pageCount) {
      setPagination({ page: pageCount, pageSize: pagination.pageSize });
    }
  }, [productsResult, total, pagination, setPagination]);

  // Derive the stable list of feed_product_ids for the current page so the
  // tags-trends and enrichment-status queries re-fire whenever the page /
  // filters change.
  const productIds = rawProducts.map((p) => p.id);

  // batchId is the job uuid, or the experiment id for experiment batches — the
  // scope picks the matching POST key. Fires only after the products page (and so
  // the batch header) resolves.
  const { data: tagsTrendsMap } = useProductTagsTrends(
    productsResult && batch ? tagsTrendsScope(batchId, batch.triggerSource) : undefined,
    productIds,
  );

  // Same job id + page-scoped id list as tags-trends: tells us which of the
  // listed products enrichment actually produced output for.
  const { data: enrichmentStatusMap } = useProductEnrichmentStatus(
    productsResult ? batchId : undefined,
    productIds,
  );

  // One merge pass folds both batch lookups onto the products list. Each field
  // is only set when its response carries the product, so a row stays untouched
  // (rather than falsely zeroed/flagged) while either call is still in flight.
  const products = rawProducts.map((p) => {
    const tt = tagsTrendsMap?.get(p.id);
    const enriched = enrichmentStatusMap?.get(p.id);
    if (!tt && enriched === undefined) return p;
    return {
      ...p,
      ...(tt ? { tags: tt.tags, trends: tt.trends } : {}),
      ...(enriched === undefined ? {} : { isEnriched: enriched }),
    };
  });
  // Backend emits live tab counts that reflect the currently-applied filter
  // set. Newly Enriched is derived as total - needsAttention rather than
  // trusting `counts.newlyEnriched` directly, since `total` already spans
  // both buckets. `undefined` hides the badge entirely (older payloads omit
  // the counts block during rollout).
  const counts = productsResult?.counts;

  // The job's own enriched figure (`deliveryHealth.productsEnriched`) and the
  // products list's newly-enriched count answer the same question from two
  // services. When they disagree, the Newly Enriched segment shows the pair as
  // `enriched/total` instead of a single number, so an enrichment shortfall is
  // visible on the tab rather than only discoverable row by row.
  //
  // Gated on an unnarrowed list: `counts` is filter-aware but
  // `productsEnriched` is batch-level, so under a filter or search the two
  // differ for a reason that has nothing to do with enrichment — comparing them
  // there would show a permanent (and numerically nonsensical) gap. `isListNarrowed`
  // is computed above (it also gates the coverage fetch this figure comes from).
  const jobEnrichedCount = batchCoverage?.productsEnriched;
  // What the job's enriched figure is compared against. An experiment enriches
  // every curated product, not only the ones new to curation (ingest mode "all"),
  // so its expected count is the tab's own `total − needsAttention`; comparing it
  // with `newlyEnriched` (curation's `new`) showed a false gap such as 10,981/2,871.
  const isExperiment = batch ? isExperimentSource(batch.triggerSource) : false;
  const expectedEnriched =
    counts === undefined
      ? undefined
      : isExperiment
        ? counts.total - counts.needsAttention
        : counts.newlyEnriched;
  const hasEnrichmentGap =
    !isListNarrowed &&
    expectedEnriched !== undefined &&
    jobEnrichedCount !== undefined &&
    jobEnrichedCount !== expectedEnriched;

  const tabCounts: Record<ProductTab, ScopeBadge | undefined> = {
    // `hasEnrichmentGap` already narrows `jobEnrichedCount` to a number — it's a
    // const conjunction that includes the undefined check.
    'newly-enriched': !counts
      ? undefined
      : hasEnrichmentGap
        ? { kind: 'ratio', enriched: jobEnrichedCount, total: expectedEnriched }
        : { kind: 'count', value: counts.total - counts.needsAttention },
    'needs-attention': counts ? { kind: 'count', value: counts.needsAttention } : undefined,
  };
  // Show placeholder once the batch has resolved without a curationRefId —
  // no curation run means no products to fetch.
  const showPlaceholder = !batchLoading && !curationRefId;

  // Trial batches don't support Needs Attention — if the URL points at it
  // (deep link, stale bookmark, back/forward), snap the tab param back to
  // Newly Enriched once the batch resolves as trial. `replace: true` avoids
  // growing history with the redirect. `effectiveTab` above already covers
  // rendering for the current pass, so this just reconciles the URL —
  // `activeTab` itself is never read again once `isTrial` is true.
  useEffect(() => {
    if (isTrial && activeTab === 'needs-attention') {
      void navigate({
        to: '/catalog-operations/$batchId/products',
        params: { batchId },
        search: { tab: 'newly-enriched' },
        replace: true,
      });
    }
  }, [isTrial, activeTab, batchId, navigate]);

  function handleTabChange(tab: ProductTab) {
    setActiveTab(tab);
    resetPage();
    void navigate({
      to: '/catalog-operations/$batchId/products',
      params: { batchId },
      search: { tab },
    });
  }

  function handleFiltersChange(filters: ProductFilterValues) {
    applyFilters(filters);
  }

  function handleSearchSubmit(value: string) {
    setSearch(value);
  }

  function handleViewModeChange(mode: 'card' | 'table') {
    // Keep the chosen page size across a view switch — the two modes
    // share one scale, so there's nothing to translate.
    setViewMode(mode, pageSize);
  }

  /** Commit the table's active sort. Sorting the current result set doesn't
   *  change how many results there are, so this doesn't reset the page. */
  function handleSortChange(nextSortBy: string | null, nextSortDir: 'asc' | 'desc') {
    setSort(nextSortBy, nextSortDir);
  }

  // ProductsListView's onPaginationChange fires onPageChange AND
  // onPageSizeChange back-to-back for a single DataTable event (even a
  // page-only change). Reading the freshest store state via getState()
  // (rather than the page/pageSize consts closed over at render time) keeps
  // the second call from clobbering the first call's update in the same tick.
  function handlePageChange(nextPage: number) {
    setPagination({
      page: nextPage,
      pageSize: useProductsListingStore.getState().pagination.pageSize,
    });
  }

  function handlePageSizeChange(nextPageSize: number) {
    setPagination({
      page: useProductsListingStore.getState().pagination.page,
      pageSize: nextPageSize,
    });
  }

  return (
    <CatalogOperationsLayout testId="products-layout">
      <div data-testid="products-page" className="mx-auto flex h-full min-h-0 flex-col gap-4">
        {/* Identity row. The trail ends in the batch itself rather than a generic
            "Products" crumb — the old layout said the same thing twice, on two
            rows. Folding them removes a row of permanent chrome without hiding
            anything, which on a page for reviewing thousands of products is the
            row that matters. */}
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2">
          <nav
            data-testid="products-breadcrumb"
            aria-label="Breadcrumb"
            className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1"
          >
            <Link
              data-testid="products-breadcrumb-catalog-operations-link"
              to="/catalog-operations"
              className="text-muted-foreground hover:text-foreground inline-flex items-center gap-1.5 text-[13px] transition-colors"
            >
              <Zap className="h-4 w-4 text-amber-500" aria-hidden="true" />
              <span>Catalog Operations</span>
            </Link>
            <ChevronRight className="text-muted-foreground/60 h-3.5 w-3.5" aria-hidden="true" />
            <Link
              data-testid="products-breadcrumb-batch-link"
              to="/catalog-operations/$batchId"
              params={{ batchId }}
              className="text-muted-foreground hover:text-foreground text-[13px] transition-colors"
            >
              Batch Details
            </Link>
            <ChevronRight className="text-muted-foreground/60 h-3.5 w-3.5" aria-hidden="true" />

            {batchLoading || batch === undefined ? (
              <Skeleton data-testid="products-header-loading" className="h-6 w-64 rounded-lg" />
            ) : (
              <span
                data-testid="products-header"
                className="flex min-w-0 flex-wrap items-center gap-2"
              >
                {/* The batch name is the page's heading and its final crumb at once. */}
                <h1
                  data-testid="products-title"
                  aria-current="page"
                  className="truncate text-base font-semibold tracking-tight"
                >
                  {batch.name || batch.batchId}
                </h1>
                <StatusChip status={batch.status} />
                <ChannelChip
                  darkBg
                  platform={batch.sourcePlatform}
                  label={batch.source}
                  testId="products-channel-chip"
                />
              </span>
            )}
          </nav>

          {batch && (
            <div data-testid="products-header-meta" className="flex shrink-0 items-center gap-2">
              <DateChip iso={batch.createdAt} testId="products-created-at-chip" />
              {batch.deliveredAt && (
                <>
                  <span className="text-muted-foreground/60">•</span>
                  <DateChip iso={batch.deliveredAt} testId="products-delivered-at-chip" />
                </>
              )}
              <span className="text-muted-foreground/60">•</span>
              <SourceDestinationChip
                sourcePlatform={batch.sourcePlatform}
                destinationPlatform={batch.destinationPlatform}
                {...batchFlowIcons(batch.triggerSource)}
                testId="products-platforms-chip"
              />
            </div>
          )}
        </div>

        {/* The scope switcher rides in the list toolbar's left slot so it shares a
            row with search/filter/view instead of sitting on its own half-width
            row above them. Trial batches only ever have one usable scope, so the
            switcher is pointless UI there and is dropped entirely. */}
        {showPlaceholder ? (
          <>
            {!isTrial && (
              <ProductScopeSwitcher
                testId="products-scope-tabs"
                tabs={visibleTabs}
                value={effectiveTab}
                counts={tabCounts}
                loading={batchLoading}
                onChange={handleTabChange}
              />
            )}
            <NoCurationPlaceholder />
          </>
        ) : (
          <ProductsListView
            columnsStorageKey="catalog-products"
            trailing={
              // Export rides the toolbar row rather than owning a header row: the
              // breadcrumb already says "Products" and the pagination footer
              // already states the range, so that row carried nothing unique.
              <ExportDownloadButton
                sourceWorkflowId={batch?.exportWorkflowId ?? null}
                label={exportLabel}
                connector={exportConnector}
                // Same run header the delivery panel narrows its attribute
                // picker off, so the export defaults to what this run actually
                // enriched instead of all 51 connector columns.
                {...(batch?.runConfig?.contentAttributes
                  ? { batchAttributeKeys: batch.runConfig.contentAttributes }
                  : {})}
                // Icon-only under 1180px — this toolbar carries the scope tabs,
                // search, filter, view toggle and export on one row, and the
                // export label is the longest thing on it.
                labelClassName="max-[1180px]:sr-only"
              />
            }
            leading={
              isTrial ? undefined : (
                <ProductScopeSwitcher
                  testId="products-scope-tabs"
                  tabs={visibleTabs}
                  value={effectiveTab}
                  counts={tabCounts}
                  loading={batchLoading}
                  onChange={handleTabChange}
                />
              )
            }
            products={products}
            total={total}
            isLoading={productsLoading}
            isFetching={productsFetching}
            hideSubtitle
            viewMode={viewMode}
            onViewModeChange={handleViewModeChange}
            page={page}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            activeTab={effectiveTab}
            filterDefinitions={filterDefinitions ?? []}
            appliedFilters={appliedFilters}
            onFiltersChange={handleFiltersChange}
            search={search}
            onSearchSubmit={handleSearchSubmit}
            onSortChange={handleSortChange}
            sortBy={sortBy}
            sortDir={sortDir}
            isControlsDisabled={batchLoading}
          />
        )}
      </div>
    </CatalogOperationsLayout>
  );
}

/**
 * Newly Enriched / Needs Attention switcher. These aren't separate panels — both
 * scopes render the same list with a different server-side filter — so this is a
 * segmented control over one list rather than a tab set, and it lives in the list
 * toolbar's left slot instead of on a row of its own.
 *
 * The count pill is passed as part of the `label` node so it can invert on the
 * active segment (white pill on the primary fill) while staying primary-on-white
 * elsewhere; `ariaLabel` carries label + count so the visual pill isn't the only
 * way to hear the number.
 */
function ProductScopeSwitcher({
  tabs,
  value,
  counts,
  loading,
  onChange,
  testId = 'products-scope-tabs',
}: {
  tabs: { value: ProductTab; label: string }[];
  value: ProductTab;
  counts: Record<ProductTab, ScopeBadge | undefined>;
  loading: boolean;
  onChange: (tab: ProductTab) => void;
  /** E2E `data-testid` for the scope tablist; each tab is `${testId}-<value>`. */
  testId?: string;
}) {
  const { t } = useTranslation('catalog-operations');
  return (
    <SegmentedTabs<ProductTab>
      value={value}
      onChange={onChange}
      ariaLabel="Product scope"
      testId={testId}
      // Trial batches show only "Newly enriched" — its count badge still matters, so keep the bar.
      showSingle
      // 420px floor at every width: the segment labels plus their count pills
      // (which can widen to an `enriched/total` ratio) stop being readable below
      // it, and the neighbouring controls compact under 1180px to make room.
      className="max-w-md min-w-[420px]"
      options={tabs.map((tab) => {
        const badge = counts[tab.value];
        // The segment's own population — the denominator for a ratio, since a
        // ratio's numerator comes from a different service and can exceed it.
        const size = badge?.kind === 'ratio' ? badge.total : badge?.value;
        // Disable on a definitive zero. `undefined` (counts still loading, or an
        // older payload without the counts block) keeps the segment interactive
        // so the control doesn't flicker.
        const disabled = loading || size === 0;
        const active = tab.value === value;
        const badgeText =
          badge === undefined
            ? null
            : badge.kind === 'ratio'
              ? t('products.enrichedOfTotal', {
                  enriched: badge.enriched.toLocaleString(),
                  total: badge.total.toLocaleString(),
                })
              : badge.value.toLocaleString();
        return {
          value: tab.value,
          disabled,
          ariaLabel:
            badge === undefined
              ? tab.label
              : badge.kind === 'ratio'
                ? t('products.enrichedOfTotalAria', {
                    label: tab.label,
                    enriched: badge.enriched.toLocaleString(),
                    total: badge.total.toLocaleString(),
                  })
                : `${tab.label}, ${badge.value.toLocaleString()}`,
          label: (
            <span className="flex items-center justify-center gap-1.5">
              {/* Skip the extra dim when the disabled tab is also the active one
                (forced via URL before counts load) — SegmentedTabs already mutes
                it on the light ramp, and double-dimming would erase it again. */}
              <span className={cn(disabled && !active && 'opacity-50')}>{tab.label}</span>
              {badgeText !== null && (
                <span
                  data-testid={`${testId}-${tab.value}-count`}
                  className={cn(
                    'rounded-full px-1.5 py-0.5 text-xs font-semibold tabular-nums',
                    active ? 'text-primary bg-white' : 'bg-primary text-white',
                  )}
                >
                  {badgeText}
                </span>
              )}
            </span>
          ),
        };
      })}
    />
  );
}

/** Empty-state shown when batch detail resolves without a `curationRefId` —
 *  no curation run means there are no products to list yet. */
function NoCurationPlaceholder() {
  return (
    <div
      data-testid="products-no-curation-empty"
      className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed bg-white px-6 py-16 text-center"
    >
      <div className="bg-muted flex h-12 w-12 items-center justify-center rounded-full">
        <PackageOpen className="text-muted-foreground h-6 w-6" aria-hidden="true" />
      </div>
      <h3 className="text-base font-semibold">No products available for this batch yet.</h3>
      <p className="text-muted-foreground max-w-md text-sm">
        Products will appear here once the batch completes curation.
      </p>
    </div>
  );
}
